import axios from "axios";
import { Company, CompanyRelation } from "../models/company-model";
import { TimeZones } from "../models/time-zones";
import { PropertyArray, PropertyRelation } from "../models/property-model";
import { AccessPointRelation } from "../models/access-points";
import { GateRelation } from "../models/gate-model";
import { StrapiUnitRelation } from "../models/units";
import { User } from "../models/user-model";
import {
  AccessGroupsRelation,
  ConfigurationAccessGroupsData,
} from "../models/access-groups";
import { RequirementTypesRelation } from "../models/requirement-types";
import { UserAccessGroupsRelation } from "../models/user-access-groups";
import { UserRoles } from "../models/role-model";
import { ConfigPropertyRelation } from "../models/config-property-model";
import { PropertyAddressMetadata } from "../models/property-model";
import { AccessPointLayoutCoordinates } from "../models/access-points";
import {
  AuthSession,
  collectUserProperties,
} from "../features/property-setup/auth";
import {
  mapPropertyToWizardState,
  PropertySetupLoadedState,
} from "../features/property-setup/utils/loadPropertySetup";

const apiToken = process.env.REACT_APP_API_TOKEN;

// Strapi 5 still wraps a single-entity create/update/find response in this
// top-level envelope (confirmed live against production) — only the entity
// shape *inside* `data` changed with the v5 migration (flattened, no more
// `.attributes`), not this outer wrapper.
interface StrapiEntityEnvelope<T> {
  data: T;
  meta: unknown;
}

//PRODUCTION
export const instance = axios.create({
  baseURL: "https://strapi.passtrack.mx",
  timeout: 30000,
});

//STAGING
// export const instance = axios.create({
//   baseURL: "https://3da75d800d10.ngrok-free.app",
//   timeout: 30000,
// });

const getCompanies = async () => {
  const { data }: { data: Company } = await instance.get(`/api/companies`, {
    headers: {
      Authorization: `Bearer ${apiToken}`,
    },
  });

  return data;
};

const getTimeZones = async () => {
  const { data }: { data: TimeZones } = await instance.get(
    `/api/time-zone-customs`,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

export interface CreateCompanyRequest {
  data: {
    name?: string;
    legal_name?: string;
  };
}

const createCompany = async (params: CreateCompanyRequest) => {
  const { data }: { data: StrapiEntityEnvelope<CompanyRelation> } = await instance.post(
    `/api/companies`,
    params,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

export interface CreatePropertyRequest {
  data: {
    name?: string;
    address?: string;
    address_metadata?: PropertyAddressMetadata;
    latitude?: number | null;
    longitude?: number | null;
    type?: "Residential" | "Industrial" | "Corporate";
    settings?: {
      has_push_notifications?: boolean;
      has_schedule_restrictions?: boolean;
      has_transactions_notifications?: boolean;
      has_secondary_user_permissions_enabled?: boolean;
      has_user_access_groups_permissions_enabled?: boolean;
      has_security_guard_transactions_permissions_enabled?: boolean;
      has_printable_pass_function?: boolean;
      has_requirements_permissions?: boolean;
    };
    passback_activated_by_default?: boolean;
    company?: number;
    license?: number;
    image?: number;
    layout?: number;
  };
}

const createProperty = async (params: CreatePropertyRequest) => {
  const { data }: { data: StrapiEntityEnvelope<PropertyRelation> } = await instance.post(
    `/api/properties`,
    params,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

export interface UpdatePropertyRequest {
  data: Partial<CreatePropertyRequest["data"]>;
}

const updateProperty = async (
  documentId: string,
  params: UpdatePropertyRequest,
) => {
  const { data }: { data: StrapiEntityEnvelope<PropertyRelation> } =
    await instance.put(`/api/properties/${documentId}`, params, {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    });

  return data;
};

const tokenHeaders = {
  Authorization: `Bearer ${apiToken}`,
};

const unwrapEntity = <T>(payload: unknown): T => {
  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as { data: T }).data;
  }

  return payload as T;
};

const unwrapList = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && typeof payload === "object" && "data" in payload) {
    const data = (payload as { data: T[] | T }).data;
    return Array.isArray(data) ? data : data ? [data] : [];
  }

  return [];
};

const PROPERTY_SETUP_POPULATE = {
  "populate[company]": true,
  "populate[image]": true,
  "populate[layout]": true,
  "populate[access_points][populate][gates]": true,
  "populate[access_points][populate][time_zone_custom]": true,
  "populate[units]": true,
  "populate[users][populate][role]": true,
  "populate[users][populate][unit]": true,
  "populate[users][populate][access_points]": true,
  "populate[access_groups][populate][access_points]": true,
  "populate[access_groups][populate][role_permissions_users]": true,
  "populate[access_groups][populate][requirement_types]": true,
};

const getUnitsForProperty = async (
  propertyId: number,
): Promise<StrapiUnitRelation[]> => {
  const { data } = await instance.get(`/api/units`, {
    params: {
      "filters[property][id][$eq]": propertyId,
      "pagination[pageSize]": 100,
    },
    headers: tokenHeaders,
  });

  return unwrapList<StrapiUnitRelation>(data);
};

const getUsersForProperty = async (propertyId: number): Promise<User[]> => {
  const { data } = await instance.get(`/api/users`, {
    params: {
      "filters[property][id][$eq]": propertyId,
      "populate[role]": true,
      "populate[unit]": true,
      "populate[access_points]": true,
      "pagination[pageSize]": 100,
    },
    headers: tokenHeaders,
  });

  return unwrapList<User>(data);
};

const getAccessGroupsForProperty = async (
  propertyId: number,
): Promise<AccessGroupsRelation[]> => {
  const { data } = await instance.get(`/api/access-groups`, {
    params: {
      "filters[properties][id][$eq]": propertyId,
      "populate[access_points]": true,
      "populate[role_permissions_users]": true,
      "populate[requirement_types]": true,
      "pagination[pageSize]": 100,
    },
    headers: tokenHeaders,
  });

  return unwrapList<AccessGroupsRelation>(data);
};

const getPropertyForSetup = async (
  documentId: string,
): Promise<PropertySetupLoadedState> => {
  const { data } = await instance.get(`/api/properties/${documentId}`, {
    params: PROPERTY_SETUP_POPULATE,
    headers: tokenHeaders,
  });

  const property = unwrapEntity<PropertyRelation>(data);
  const [units, users, accessGroups] = await Promise.all([
    getUnitsForProperty(property.id),
    getUsersForProperty(property.id),
    getAccessGroupsForProperty(property.id),
  ]);

  return mapPropertyToWizardState(property, {
    units,
    users,
    accessGroups,
  });
};

export interface CreateConfigPropertyRequest {
  data: {
    users?: number[];
    property?: number;
  };
}

const createConfigProperty = async (params: CreateConfigPropertyRequest) => {
  const { data }: { data: StrapiEntityEnvelope<ConfigPropertyRelation> } =
    await instance.post(`/api/config-properties`, params, {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    });

  return data;
};

export interface CreateAccessPointRequest {
  data: {
    name?: string;
    vanue_name?: string;
    longitude?: number;
    latitude?: number;
    property?: number;
    time_zone_custom?: number;
    layout_coordinates?: AccessPointLayoutCoordinates | null;
  };
}

const createAccessPoint = async (params: CreateAccessPointRequest) => {
  const { data }: { data: StrapiEntityEnvelope<AccessPointRelation> } = await instance.post(
    `/api/access-points`,
    params,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

const updateAccessPoint = async (
  documentId: string,
  params: CreateAccessPointRequest,
) => {
  const { data }: { data: StrapiEntityEnvelope<AccessPointRelation> } =
    await instance.put(`/api/access-points/${documentId}`, params, {
      headers: tokenHeaders,
    });

  return data;
};

const deleteAccessPoint = async (documentId: string) => {
  await instance.delete(`/api/access-points/${documentId}`, {
    headers: tokenHeaders,
  });
};

export interface CreateGateRequest {
  data: {
    name?: string;
    type?: "entrance" | "exit";
    access_point?: number;
  };
}

const createGate = async (params: CreateGateRequest) => {
  const { data }: { data: StrapiEntityEnvelope<GateRelation> } = await instance.post(
    `/api/gates`,
    params,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

const updateGate = async (documentId: string, params: CreateGateRequest) => {
  const { data }: { data: StrapiEntityEnvelope<GateRelation> } =
    await instance.put(`/api/gates/${documentId}`, params, {
      headers: tokenHeaders,
    });

  return data;
};

const deleteGate = async (documentId: string) => {
  await instance.delete(`/api/gates/${documentId}`, {
    headers: tokenHeaders,
  });
};

export interface CreateUnitRequest {
  data: {
    name?: string;
    property?: number;
    access_points?: number[];
    is_admin?: boolean;
  };
}

const createUnit = async (params: CreateUnitRequest) => {
  const { data }: { data: StrapiEntityEnvelope<StrapiUnitRelation> } = await instance.post(
    `/api/units`,
    params,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

const updateUnit = async (documentId: string, params: CreateUnitRequest) => {
  const { data }: { data: StrapiEntityEnvelope<StrapiUnitRelation> } =
    await instance.put(`/api/units/${documentId}`, params, {
      headers: tokenHeaders,
    });

  return data;
};

const deleteUnit = async (documentId: string) => {
  await instance.delete(`/api/units/${documentId}`, {
    headers: tokenHeaders,
  });
};

export interface CreateUserPayload {
  username?: string;
  name?: string;
  email?: string;
  phone?: string;
  unit?: number;
  property?: number | null;
  company?: number;
  password?: string;
  access_points?: number[];
  created_by_users?: number[];
  blocked?: boolean;
  last_access_selected_id?: number;
  user_access_groups?: number[];
  license?: number;
  deleted_at?: string | null;
  confirmed?: boolean;
}

export interface UserCreatedResponse {
  user: User;
  confirmation_token?: String;
}

const createUser = async (params: CreateUserPayload) => {
  const { data }: { data: UserCreatedResponse } = await instance.post(
    `/api/custom-auth/custom-register`,
    params,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

const addUserRole = async (userId: number, role: number) => {
  const { data }: { data: User } = await instance.put(
    `/api/users/${userId}`,
    {
      role,
    },
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

const sendConfirmationEmail = async (params: {
  email: string;
  confirmation_token: string;
}) => {
  const { data } = await instance.post(
    `/api/custom-auth/send-email`,
    {
      email: params.email.trim(),
      confirmation_token: params.confirmation_token,
    },
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

export interface UpdateUserPayload {
  username?: string;
  name?: string;
  email?: string;
  phone?: string;
  unit?: number | null;
  property?: number | null;
  company?: number | null;
  access_points?: number[];
  role?: number;
  blocked?: boolean;
  confirmed?: boolean;
  license?: number;
}

const updateUser = async (userId: number, params: UpdateUserPayload) => {
  const { data }: { data: User } = await instance.put(
    `/api/users/${userId}`,
    params,
    {
      headers: tokenHeaders,
    },
  );

  return data;
};

const deleteUser = async (userId: number) => {
  await instance.delete(`/api/users/${userId}`, {
    headers: tokenHeaders,
  });
};

const getUsersByEmail = async (email: string): Promise<User[]> => {
  const { data } = await instance.get(`/api/users`, {
    params: {
      "filters[email][$eqi]": email.trim(),
    },
    headers: {
      Authorization: `Bearer ${apiToken}`,
    },
  });

  if (Array.isArray(data)) {
    return data;
  }

  if (data && Array.isArray((data as { data?: User[] }).data)) {
    return (data as { data: User[] }).data;
  }

  return [];
};

export interface CreateAccGroupRequest {
  data: {
    name?: string;
    company?: number | null;
    properties?: number[];
    access_points?: number[];
    role_permissions_users?: number[];
    configuration?: ConfigurationAccessGroupsData[];
    banned_users?: number[];
    unit_access_group?: boolean;
  };
}

const createAccessGroup = async (params: CreateAccGroupRequest) => {
  const { data }: { data: StrapiEntityEnvelope<AccessGroupsRelation> } =
    await instance.post(`/api/access-groups`, params, {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    });

  return data;
};

const updateAccessGroup = async (
  documentId: string,
  params: CreateAccGroupRequest,
) => {
  const { data }: { data: StrapiEntityEnvelope<AccessGroupsRelation> } =
    await instance.put(`/api/access-groups/${documentId}`, params, {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    });

  return data;
};

const deleteAccessGroup = async (documentId: string) => {
  await instance.delete(`/api/access-groups/${documentId}`, {
    headers: tokenHeaders,
  });
};

export interface CreateRequirementTypeRequest {
  data: {
    name?: string;
    description?: string;
    data_type?:
      | "photo"
      | "webhook"
      | "string"
      | "document"
      | "boolean"
      | "date"
      | "selector";
    required?: boolean;
    access_key?: boolean;
    transaction?: boolean;
    selector_data?: string[];
    access_groups?: number[];
    url?: string;
  };
}

const createRequirementType = async (params: CreateRequirementTypeRequest) => {
  const { data }: { data: StrapiEntityEnvelope<RequirementTypesRelation> } =
    await instance.post(`/api/requirement-types`, params, {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    });

  return data;
};

export interface CreateUserAccGroupRequest {
  data: {
    user?: number;
    access_group?: number;
    access?: boolean;
  };
}

const createUserAccessGroup = async (params: CreateUserAccGroupRequest) => {
  const { data }: { data: StrapiEntityEnvelope<UserAccessGroupsRelation> } =
    await instance.post(`/api/user-access-groups`, params, {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    });

  return data;
};

const getRoles = async (): Promise<UserRoles> => {
  const { data }: { data: UserRoles } = await instance.get(
    `/api/users-permissions/roles`,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return data;
};

export interface LoginRequest {
  identifier: string;
  password: string;
}

export interface LoginResponse {
  jwt: string;
  user: User;
}

const login = async (email: string, password: string): Promise<LoginResponse> => {
  const { data }: { data: LoginResponse } = await instance.post(
    `/api/auth/local`,
    {
      identifier: email.trim(),
      password,
    },
  );

  return data;
};

const authHeaders = (jwt: string) => ({
  Authorization: `Bearer ${jwt}`,
});

const unwrapUser = (payload: unknown): User => {
  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as { data: User }).data;
  }

  return payload as User;
};

const getAuthenticatedUser = async (jwt: string): Promise<User> => {
  const { data } = await instance.get(`/api/users/me`, {
    params: {
      "populate[role]": true,
      "populate[property]": true,
      "populate[company][populate][properties]": true,
    },
    headers: authHeaders(jwt),
  });

  return unwrapUser(data);
};

const getPropertiesForUser = async (
  jwt: string,
  userId: number,
): Promise<PropertyRelation[]> => {
  const { data }: { data: PropertyArray | PropertyRelation[] } =
    await instance.get(`/api/properties`, {
      params: {
        "filters[users][id][$eq]": userId,
        "pagination[pageSize]": 100,
      },
      headers: authHeaders(jwt),
    });

  if (Array.isArray(data)) {
    return data;
  }

  return data?.data ?? [];
};

const unwrapConfigProperties = (
  payload: unknown,
): ConfigPropertyRelation[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && typeof payload === "object" && "data" in payload) {
    const data = (payload as { data: ConfigPropertyRelation[] | ConfigPropertyRelation })
      .data;
    return Array.isArray(data) ? data : data ? [data] : [];
  }

  return [];
};

const getConfigPropertiesForUser = async (
  jwt: string,
  userId: number,
): Promise<ConfigPropertyRelation[]> => {
  const { data } = await instance.get(`/api/config-properties`, {
    params: {
      "filters[users][id][$eq]": userId,
      "populate[property]": true,
      "pagination[pageSize]": 100,
    },
    headers: authHeaders(jwt),
  });

  return unwrapConfigProperties(data);
};

const fetchOptional = async <T>(
  label: string,
  fetcher: () => Promise<T>,
  fallback: T,
): Promise<T> => {
  try {
    return await fetcher();
  } catch (error) {
    console.warn(`No se pudieron cargar ${label}:`, error);
    return fallback;
  }
};

const buildUserProperties = async (
  jwt: string,
  user: User,
): Promise<ReturnType<typeof collectUserProperties>> => {
  const userId = user.id;
  if (!userId) {
    return collectUserProperties(user);
  }

  const linkedProperties = await fetchOptional(
    "propiedades del usuario",
    () => getPropertiesForUser(jwt, userId),
    [],
  );
  const configProperties = await fetchOptional(
    "config-properties",
    () => getConfigPropertiesForUser(jwt, userId),
    [],
  );

  return collectUserProperties(user, linkedProperties, configProperties);
};

export interface RefreshUserSessionResult {
  user: User;
  properties: ReturnType<typeof collectUserProperties>;
}

const refreshUserSession = async (
  jwt: string,
  currentUser: User,
): Promise<RefreshUserSessionResult> => {
  const user = await fetchOptional(
    "perfil del usuario",
    () => getAuthenticatedUser(jwt),
    currentUser,
  );
  const userId = user.id ?? currentUser.id;
  const mergedUser = {
    ...currentUser,
    ...user,
    id: userId,
    role: user.role ?? currentUser.role,
    property: user.property ?? currentUser.property,
    company: user.company ?? currentUser.company,
  };

  const properties = userId
    ? await buildUserProperties(jwt, mergedUser)
    : collectUserProperties(mergedUser);

  return {
    user: mergedUser,
    properties,
  };
};

export interface AuthLoginResult extends AuthSession {
  properties: ReturnType<typeof collectUserProperties>;
}

const loadAuthSession = async (
  email: string,
  password: string,
): Promise<AuthLoginResult> => {
  const { jwt, user: loginUser } = await login(email, password);
  const user = await fetchOptional(
    "perfil del usuario",
    () => getAuthenticatedUser(jwt),
    loginUser,
  );
  const userId = user.id ?? loginUser.id;
  const mergedUser = {
    ...loginUser,
    ...user,
    id: userId,
    role: user.role ?? loginUser.role,
    property: user.property ?? loginUser.property,
    company: user.company ?? loginUser.company,
  };

  const properties = userId
    ? await buildUserProperties(jwt, mergedUser)
    : collectUserProperties(mergedUser);

  return {
    jwt,
    user: mergedUser,
    properties,
  };
};

const api = {
  getCompanies,
  getTimeZones,
  createCompany,
  createProperty,
  updateProperty,
  getPropertyForSetup,
  createConfigProperty,
  createAccessPoint,
  updateAccessPoint,
  deleteAccessPoint,
  createGate,
  updateGate,
  deleteGate,
  createUnit,
  updateUnit,
  deleteUnit,
  createUser,
  updateUser,
  deleteUser,
  addUserRole,
  sendConfirmationEmail,
  getUsersByEmail,
  createAccessGroup,
  updateAccessGroup,
  deleteAccessGroup,
  createRequirementType,
  createUserAccessGroup,
  getRoles,
  login,
  getAuthenticatedUser,
  getPropertiesForUser,
  getConfigPropertiesForUser,
  refreshUserSession,
  loadAuthSession,
};

export default api;
